import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "../../fields/base/TextField";
import api from "../../../utils/api";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { setCurrentUser } from "../../../redux/reducers/userReducer";
import { setChannel } from "../../../redux/reducers/channel";
import { setMarketPlace } from "../../../redux/reducers/marketPlace";
import { useAppDispatch } from "../../../redux/hooks";
import { Modal } from "../../core/basic/Modal";
import { Button } from "../../core/basic/Button";
import { HStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";

const AdditionalWalmartOrgModal = ({ isOpen, onOpenChange }) => {
  const { t } = useTranslation(["onboarding", "common"]);
  const [newOrgName, setNewOrgName] = useState("");
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const createNewWalmartOrg = useMutation({
    mutationFn: async () => {
      const formattedOrgName = newOrgName.toLowerCase().includes("walmart") ? newOrgName : `${newOrgName} - Walmart`;

      return await api.post("/walmart/auth/onboarding", {
        action: "add_new_walmart_profile",
        new_org_name: formattedOrgName,
      });
    },

    default: [],

    onSuccess: async (creationRes) => {
      // Change user profile to point towards new walmart org to finish onboarding
      const org_id = creationRes.data.org_id;
      const res = await api.put("/api/organizations/", { id: org_id });

      // Update redux and localStorage
      localStorage.setItem("Marketplace", "USA");
      localStorage.setItem("channel", "walmart");

      dispatch(
        setCurrentUser({
          current_profile: res.data.profile_name,
        }),
      );
      dispatch(setMarketPlace("USA"));
      dispatch(setChannel("walmart"));

      // Redirect to seller type dialogue in onboarding flow
      // Full page load; router history writes are deferred, so push + window.location.reload() would reload the old URL
      navigate({ to: "/onboarding/v2/sellertype", reloadDocument: true });
    },

    onError: (error) => {
      setError(error.response?.data?.error);
    },
  });

  const handleClose = () => {
    setNewOrgName("");
    setError(null);
    onOpenChange?.(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      title={t(($) => $.onboarding["Add an Additional Walmart Organization"])}
      css={{ display: "flex", flexDirection: "column", width: "60vw", maxHeight: "90vh" }}
    >
      <div
        className={css({
          flex: "1 1 auto",
          padding: "20px 10px",
          textAlign: "center",
          overflowY: "auto",
        })}
      >
        <TextField
          label={t(($) => $.onboarding["New Organization Name"])}
          placeholder={t(($) => $.onboarding["Enter the organization name"])}
          value={newOrgName}
          onChange={setNewOrgName}
          errorMessage={error}
          css={{ maxWidth: "400px", margin: "0 auto" }}
        />
      </div>
      <HStack justifyContent="space-around">
        <Button variant="secondary" onPress={handleClose}>
          {t(($) => $.common.actions.Cancel)}
        </Button>
        <Button onPress={() => createNewWalmartOrg.mutate()}>{t(($) => $.onboarding.Confirm)}</Button>
      </HStack>
    </Modal>
  );
};

export default AdditionalWalmartOrgModal;
